/**
 * Construction du CSV des 2 tableaux de l'onglet "Statistiques et besoins" (acheteur) :
 * l'estimation financiere des besoins (par lot technique) et la localisation en % du
 * volume de commande (par departement, groupe par region). Les annees passees viennent
 * du reel (passe), l'annee en cours + futures des previsions saisies.
 */

interface PiedAnnee {
  total_financier: number;
  nombre: number;
  bc: number;
  ms: number;
  montant_moyen: number;
}
interface EstimationData {
  annees: number[];
  lots: Record<string, Record<string, number>>;
  pied: Record<string, PiedAnnee>;
  previsions: Record<string, Record<string, number>>;
}
interface DepZone { id: number; nom: string; }
interface RegionZone { id_region: number; region_nom: string; departements: DepZone[]; }
interface LocalisationData {
  annees: number[];
  zones: RegionZone[];
  passe: Record<string, Record<string, number>>;
  previsions: Record<string, Record<string, number>>;
}
interface LotTech { id: number; code: string; nom: string; }

const SEP = ';';
const escape = (v: string | number): string => {
  const s = String(v ?? '');
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const row = (cells: (string | number)[]): string => cells.map(escape).join(SEP);

// Plage d'annees affichee : plus ancienne annee passee avec donnees -> annee en cours + 3.
const yearRange = (annees: number[] | undefined, cur: number): number[] => {
  const pasts = (annees ?? []).filter((y) => y < cur);
  const start = pasts.length ? Math.min(...pasts) : cur;
  const out: number[] = [];
  for (let y = start; y <= cur + 3; y++) out.push(y);
  return out;
};

export function buildStatsBesoinsCsv(
  estimation: EstimationData | null,
  localisation: LocalisationData | null,
  lotsTech: LotTech[],
): string {
  const cur = new Date().getFullYear();
  const lines: string[] = [];

  // -------- Tableau 1 : estimation financiere des besoins (en euros) --------
  const estYears = yearRange(estimation?.annees, cur);
  lines.push(row(['Statistiques et estimation financiere des besoins (en euros)']));
  lines.push(row(['N°', 'Lot technique', ...estYears]));
  (lotsTech ?? []).forEach((lt) => {
    const code = String(lt.code);
    const cells: (string | number)[] = [lt.code, lt.nom];
    estYears.forEach((y) => {
      const v = y < cur
        ? estimation?.lots?.[code]?.[String(y)]
        : estimation?.previsions?.[code]?.[String(y)];
      cells.push(v == null ? '' : Math.round(Number(v)));
    });
    lines.push(row(cells));
  });
  const pied = estimation?.pied ?? {};
  const piedCell = (y: number, pick: (p: PiedAnnee) => number): string | number =>
    (y < cur ? Math.round(pick(pied[String(y)] ?? { total_financier: 0, nombre: 0, bc: 0, ms: 0, montant_moyen: 0 })) : '');
  lines.push(row(['', 'Total financier', ...estYears.map((y) => piedCell(y, (p) => p.total_financier))]));
  lines.push(row(['', 'Nombre de commandes', ...estYears.map((y) => piedCell(y, (p) => p.nombre))]));
  lines.push(row(['', 'BC', ...estYears.map((y) => piedCell(y, (p) => p.bc))]));
  lines.push(row(['', 'MS', ...estYears.map((y) => piedCell(y, (p) => p.ms))]));
  lines.push(row(['', 'Montant moyen par commande', ...estYears.map((y) => piedCell(y, (p) => p.montant_moyen))]));

  lines.push('');

  // -------- Tableau 2 : localisation en % du volume de commande --------
  const locYears = yearRange(localisation?.annees, cur);
  const pct = (depId: number, y: number): number => {
    const v = y < cur
      ? localisation?.passe?.[String(depId)]?.[String(y)]
      : localisation?.previsions?.[String(depId)]?.[String(y)];
    return Number(v ?? 0);
  };
  lines.push(row(['Localisation en % du volume de commande']));
  lines.push(row(['N°', 'Lots geographiques', ...locYears]));
  (localisation?.zones ?? []).forEach((zone) => {
    // Ligne region : agregat (somme des departements).
    lines.push(row([
      zone.id_region,
      zone.region_nom,
      ...locYears.map((y) => Math.round(zone.departements.reduce((s, d) => s + pct(d.id, y), 0) * 10) / 10),
    ]));
    zone.departements.forEach((d) => {
      lines.push(row([d.id, d.nom, ...locYears.map((y) => Math.round(pct(d.id, y) * 10) / 10)]));
    });
  });

  // BOM UTF-8 pour qu'Excel ouvre les accents correctement.
  return '\uFEFF' + lines.join('\n');
}
